import React, { useEffect, useState, useCallback } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

const emptyForm = {
  vendor_code: '',
  vendor_name: '',
  legal_name: '',
  gstin: '',
  pan: '',
  is_msme: false,
  msme_udyam_no: '',
  tds_section_code: '',
  tds_default_rate_pct: '',
  is_rcm_applicable: false,
  is_itc_eligible_default: true,
  contact_person: '',
  phone: '',
  email: '',
  address_line1: '',
  city: '',
  state: '',
  pincode: '',
  default_payment_terms_days: 30,
  status: 'ACTIVE',
  blacklist_reason: ''
};

const TDS_SECTIONS = ['', '94C', '94J', '94I', '94H', '94A'];
const STATUSES = ['ACTIVE', 'INACTIVE', 'BLACKLISTED'];

function PmsVendorMaster({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const loadData = useCallback(async () => {
    if (!selectedProperty?.id) { setRows([]); return; }
    try {
      const qs = new URLSearchParams();
      if (search) qs.append('search', search);
      if (statusFilter) qs.append('status', statusFilter);
      const res = await fetch(`/api/v1/expense/vendors?${qs.toString()}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load');
      setRows(data.rows || []);
      setError('');
    } catch { setError('Network error'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id, search, statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const resetForm = () => { setForm(emptyForm); setEditingId(''); };

  const startEdit = async (v) => {
    try {
      const res = await fetch(`/api/v1/expense/vendors/${v.vendor_id}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed');
      setForm({
        vendor_code: data.vendor_code || '',
        vendor_name: data.vendor_name || '',
        legal_name: data.legal_name || '',
        gstin: data.gstin || '',
        pan: data.pan || '',
        is_msme: !!data.is_msme,
        msme_udyam_no: data.msme_udyam_no || '',
        tds_section_code: data.tds_section_code || '',
        tds_default_rate_pct: data.tds_default_rate_pct || '',
        is_rcm_applicable: !!data.is_rcm_applicable,
        is_itc_eligible_default: data.is_itc_eligible_default !== false,
        contact_person: data.contact_person || '',
        phone: data.phone || '',
        email: data.email || '',
        address_line1: data.address_line1 || '',
        city: data.city || '',
        state: data.state || '',
        pincode: data.pincode || '',
        default_payment_terms_days: data.default_payment_terms_days || 30,
        status: data.status || 'ACTIVE',
        blacklist_reason: data.blacklist_reason || ''
      });
      setEditingId(v.vendor_id);
      scrollToForm();
    } catch { setError('Network error'); }
  };

  const save = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    if (!form.vendor_code || !form.vendor_name) return setError('Vendor code and name required');
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        ...form,
        tds_default_rate_pct: form.tds_default_rate_pct === '' ? null : Number(form.tds_default_rate_pct),
        default_payment_terms_days: Number(form.default_payment_terms_days) || 30
      };
      const url = editingId ? `/api/v1/expense/vendors/${editingId}` : '/api/v1/expense/vendors';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Save failed');
      setMessage(editingId ? 'Vendor updated.' : 'Vendor created.');
      resetForm();
      loadData();
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  const inputStyle = { padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' };
  const labelStyle = { display: 'grid', gap: '0.25rem', fontSize: '0.8rem' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Vendor Master</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{error}</div>}
      {message && <div style={{ background: '#dcfce7', color: '#166534', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{message}</div>}

      <div ref={formRef} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginBottom: '0.8rem', ...editHighlightStyle(editHighlight) }}>
        <h3 style={{ margin: '0 0 0.7rem 0', fontSize: '1rem' }}>{editingId ? 'Edit Vendor' : 'New Vendor'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.7rem' }}>
          <label style={labelStyle}><span>Vendor Code *</span><input value={form.vendor_code} onChange={e => set('vendor_code', e.target.value)} disabled={!!editingId} style={inputStyle} /></label>
          <label style={labelStyle}><span>Vendor Name *</span><input value={form.vendor_name} onChange={e => set('vendor_name', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>Legal Name</span><input value={form.legal_name} onChange={e => set('legal_name', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>GSTIN</span><input value={form.gstin} onChange={e => set('gstin', e.target.value.toUpperCase())} maxLength={15} style={inputStyle} /></label>
          <label style={labelStyle}><span>PAN</span><input value={form.pan} onChange={e => set('pan', e.target.value.toUpperCase())} maxLength={10} style={inputStyle} /></label>
          <label style={labelStyle}><span>TDS Section</span>
            <select value={form.tds_section_code} onChange={e => set('tds_section_code', e.target.value)} style={inputStyle}>
              {TDS_SECTIONS.map(s => <option key={s} value={s}>{s || '— None —'}</option>)}
            </select>
          </label>
          <label style={labelStyle}><span>TDS Rate (%)</span><input type="number" step="0.01" value={form.tds_default_rate_pct} onChange={e => set('tds_default_rate_pct', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>Contact Person</span><input value={form.contact_person} onChange={e => set('contact_person', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>Phone</span><input value={form.phone} onChange={e => set('phone', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>Email</span><input value={form.email} onChange={e => set('email', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>City</span><input value={form.city} onChange={e => set('city', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>State</span><input value={form.state} onChange={e => set('state', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>Pincode</span><input value={form.pincode} onChange={e => set('pincode', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>Payment Terms (days)</span><input type="number" value={form.default_payment_terms_days} onChange={e => set('default_payment_terms_days', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>Status</span>
            <select value={form.status} onChange={e => set('status', e.target.value)} style={inputStyle}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          {form.status === 'BLACKLISTED' && (
            <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
              <span>Blacklist Reason</span>
              <input value={form.blacklist_reason} onChange={e => set('blacklist_reason', e.target.value)} style={inputStyle} />
            </label>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
            <input type="checkbox" checked={!!form.is_msme} onChange={e => set('is_msme', e.target.checked)} />
            <span>MSME</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
            <input type="checkbox" checked={!!form.is_rcm_applicable} onChange={e => set('is_rcm_applicable', e.target.checked)} />
            <span>RCM Applicable</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
            <input type="checkbox" checked={!!form.is_itc_eligible_default} onChange={e => set('is_itc_eligible_default', e.target.checked)} />
            <span>ITC eligible by default</span>
          </label>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.8rem' }}>
          {editingId && <button onClick={resetForm} style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, background: '#f9fafb', cursor: 'pointer' }}>Cancel</button>}
          <button onClick={save} disabled={saving} style={{ padding: '0.5rem 1.2rem', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {saving ? 'Saving…' : (editingId ? 'Update' : 'Create')}
          </button>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '0.6rem 1rem', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: '0.6rem', alignItems: 'center', background: '#fafafa' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name/code/GSTIN" style={{ ...inputStyle, flex: 1, maxWidth: 280 }} />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={inputStyle}>
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#6b7280' }}>{rows.length} vendor{rows.length === 1 ? '' : 's'}</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem' }}>Code</th>
                <th style={{ padding: '0.5rem' }}>Name</th>
                <th style={{ padding: '0.5rem' }}>GSTIN</th>
                <th style={{ padding: '0.5rem' }}>TDS</th>
                <th style={{ padding: '0.5rem' }}>Phone</th>
                <th style={{ padding: '0.5rem' }}>Status</th>
                <th style={{ padding: '0.5rem' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>No vendors found.</td></tr>
              ) : rows.map(v => (
                <tr key={v.vendor_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{v.vendor_code}</td>
                  <td style={{ padding: '0.5rem' }}>{v.vendor_name}{v.is_msme && <span style={{ marginLeft: 6, fontSize: '0.7rem', padding: '1px 6px', borderRadius: 4, background: '#fef3c7', color: '#92400e' }}>MSME</span>}</td>
                  <td style={{ padding: '0.5rem' }}>{v.gstin || '—'}</td>
                  <td style={{ padding: '0.5rem' }}>{v.tds_section_code || '—'}</td>
                  <td style={{ padding: '0.5rem' }}>{v.phone || '—'}</td>
                  <td style={{ padding: '0.5rem' }}><span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 999, background: v.status === 'ACTIVE' ? '#dcfce7' : v.status === 'BLACKLISTED' ? '#fee2e2' : '#f3f4f6', color: v.status === 'ACTIVE' ? '#166534' : v.status === 'BLACKLISTED' ? '#991b1b' : '#374151' }}>{v.status}</span></td>
                  <td style={{ padding: '0.5rem' }}><button onClick={() => startEdit(v)} style={{ padding: '0.3rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: '0.78rem' }}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PmsVendorMaster;
